import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useToast } from '../../../../context/ToastContext';
import apiService from '../../../../services/apiService';
import { Button, MultiselectDropdown } from '../../../../components/ui';
import { FaSave, FaTimes } from 'react-icons/fa';

const breadcrumb = (
  <span>
    <span className="cursor-pointer hover:text-blue-600" onClick={() => window.history.back()}>
      Home
    </span> &nbsp;/&nbsp; 
    <span className="cursor-pointer hover:text-blue-600" onClick={() => window.history.back()}>
      Courses
    </span> &nbsp;/&nbsp; 
    <span className="text-gray-700">Edit</span>
  </span>
);

const EditCourse = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSuccess, showError } = useToast();
  const { register, handleSubmit, formState: { errors, isSubmitting }, setValue, getValues, reset, watch } = useForm();
  
  const [loading, setLoading] = useState(true);
  const [eduStandards, setEduStandards] = useState([]);
  const [activeTab, setActiveTab] = useState('basic');
  const [course, setCourse] = useState(null);
  const [selectedStandards, setSelectedStandards] = useState([]);

  // Fetch course data
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);
        // Fetch course with eduStandards populated
        const apiUrl = `/i/ims/course/${id}?populate=eduStandard`;
        console.log('Fetching course data from:', apiUrl);
        const data = await apiService.request(apiUrl);
        
        if (data) {
          console.log('Course data received:', data);
          setCourse(data);
          
          // Get all eduStandards
          let selectedEduStandards = [];
          
          // Add all eduStandards
          if (Array.isArray(data.eduStandards)) {
            console.log('eduStandards array found:', data.eduStandards);
            data.eduStandards.forEach(es => {
              // Handle both string IDs and object formats
              const standardId = typeof es === 'string' ? es : es?.id;
              if (standardId) {
                console.log('Adding eduStandard:', standardId);
                selectedEduStandards.push(standardId);
              }
            });
          } else {
            console.log('No eduStandards array found in response');
          }
          
          // Set selected standards for the multiselect
          console.log('Setting selected standards:', selectedEduStandards);
          setSelectedStandards(selectedEduStandards);
          
          // Set form values
          const formValues = {
            name: data.name || '',
            code: data.code || '',
            uri: data.uri || '',
            status: data.status,
            order: data.order || 0,
            thumb: data.thumb || '',
            // Set the eduStandards field with all selected standards
            eduStandards: selectedEduStandards,
            metaTags: {
              keywords: data.metaTags?.keywords || '',
              ogDescription: data.metaTags?.ogDescription || '',
              ogImage: data.metaTags?.ogImage || '',
              ogTitle: data.metaTags?.ogTitle || ''
            }
          };
          
          console.log('Setting form values:', formValues);
          reset(formValues);
        }
      } catch (error) {
        console.error('Error fetching course:', error);
        showError('Failed to load course data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCourse();
  }, [id, reset, showError]);

  // Handle education standards change
  const handleStandardsChange = (newValues) => {
    console.log('Education standards changed:', newValues);
    // Update the form's eduStandards field
    setValue('eduStandards', newValues, { shouldValidate: true, shouldDirty: true });
    // Update the selected standards state
    setSelectedStandards(newValues);
    
    console.log('Form values after change:', {
      eduStandards: getValues('eduStandards')
    });
  };

  // Fetch education standards
  useEffect(() => {
    const fetchEduStandards = async () => {
      try {
        console.log('Fetching education standards...');
        const data = await apiService.request('/i/ims/edustandard?limit=all&status=true&select=name');
        console.log('Education standards received:', data);
        
        if (Array.isArray(data)) {
          console.log(`Found ${data.length} education standards`);
          setEduStandards(data);
        } else {
          console.error('Education standard data is not an array:', data);
          setEduStandards([]);
        }
      } catch (error) {
        console.error('Error fetching education standards:', error);
        setEduStandards([]);
      }
    };

    fetchEduStandards();
  }, []);

  const onSubmit = async (formData) => {
    try {
      setLoading(true);
      
      // Create a clean copy of form data
      const payload = { ...formData };
      payload.eduStandards = selectedStandards || [];
      payload.eduStandard = selectedStandards[0] || null;
            
      

      
      // Clean up the payload - remove any undefined or null values
      Object.keys(payload).forEach(key => {
        if (payload[key] === undefined || payload[key] === null) {
          delete payload[key];
        }
      });
      
      // Log the payload for debugging
      console.log('Submitting course data:', {
        ...payload,
        // Log the education standards for clarity
        eduStandard: payload.eduStandard,
        eduStandards: payload.eduStandards
      });
      
      // Make the API request
      const response = await apiService.request(`/i/ims/course/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      console.log('Update successful, response:', response);
      
      // Show success message and redirect
      showSuccess('Course updated successfully');
      navigate('/course-programs/courses');
      
    } catch (error) {
      console.error('Error updating course:', {
        error,
        message: error.message,
        response: error.response
      });
      
      // Extract error message from response if available
      let errorMessage = 'Failed to update course. Please try again.';
      try {
        const errorData = await error.response?.json?.();
        if (errorData?.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        console.error('Error parsing error response:', e);
      }
      
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !course) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="border-b border-gray-200">
        <div className="p-6">
          <nav className="text-xs text-gray-500 mb-4" aria-label="Breadcrumb">
            {breadcrumb}
          </nav>
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Edit Course</h1>
          </div>
          
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px space-x-8">
              <button
                onClick={() => setActiveTab('basic')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'basic'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Basic Details
              </button>
              <button
                onClick={() => setActiveTab('meta')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'meta'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Meta Tags
              </button>
            </nav>
          </div>
        </div>
        
        <div className="p-6">
          <form onSubmit={handleSubmit(onSubmit)}>
            {activeTab === 'basic' && (
              <div className="mb-8">
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="name">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    {...register('name', { required: 'Name is required' })}
                    placeholder="Enter course name"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="uri">
                    URI <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="uri"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    {...register('uri', { required: 'URI is required' })}
                    placeholder="e.g., class-10th"
                  />
                  {errors.uri && (
                    <p className="text-red-500 text-sm mt-1">{errors.uri.message}</p>
                  )}
                </div>

                <MultiselectDropdown
                  label="Education Standards"
                  options={eduStandards}
                  selectedValues={selectedStandards}
                  onChange={handleStandardsChange}
                  placeholder="Select education standards"
                  disabled={eduStandards.length === 0}
                  primaryLabel="(Primary)"
                  showPrimary={true}
                />

                <div>
                <label className="block text-gray-700 mb-2" htmlFor="thumb">
                    Thumb
                  </label>                  
                  <input
                    id="thumb"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    placeholder="Image URL"
                    {...register('thumb')}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="order">
                    Order
                  </label>
                  <input
                    id="order"
                    type="number"
                    className="w-full border rounded px-3 py-2"
                    placeholder="Display order"
                    {...register('order')}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="status">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center">
                    <div className="flex items-center mr-4">
                      <input
                        id="statusActive"
                        type="radio"
                        name="status"
                        className="mr-1"
                        checked={watch('status') === true || watch('status') === 'true'}
                        onChange={() => {
                          setValue('status', true);
                          console.log('Set status to true');
                        }}
                      />
                      <label htmlFor="statusActive" className="ml-1">Active</label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="statusInactive"
                        type="radio"
                        name="status"
                        className="mr-1"
                        checked={watch('status') === false || watch('status') === 'false'}
                        onChange={() => {
                          setValue('status', false);
                          console.log('Set status to false');
                        }}
                      />
                      <label htmlFor="statusInactive" className="ml-1">Inactive</label>
                    </div>
                  </div>
                  {errors.status && (
                    <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'meta' && (
              <div className="mb-8">
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="metaTags.ogTitle">
                    OG Title
                  </label>
                  <input
                    id="metaTags.ogTitle"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    {...register('metaTags.ogTitle')}
                    placeholder="e.g., My Awesome Course"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="metaTags.keywords">
                    Keywords
                  </label>
                  <input
                    id="metaTags.keywords"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    {...register('metaTags.keywords')}
                    placeholder="e.g., education, course"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="metaTags.ogDescription">
                    OG Description
                  </label>
                  <textarea
                    id="metaTags.ogDescription"
                    className="w-full border rounded px-3 py-2"
                    rows="2"
                    {...register('metaTags.ogDescription')}
                    placeholder="Enter meta description"
                  ></textarea>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="metaTags.ogImage">
                    OG Image
                  </label>
                  <input
                    id="metaTags.ogImage"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    placeholder="Image URL"
                    {...register('metaTags.ogImage')}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-between mt-6">
              <Button
                type="button"
                onClick={() => navigate('/course-programs/courses')}
                variant="outline"
                size="md"
                className="border-gray-300 text-gray-700"
                leftIcon={<FaTimes />}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={isSubmitting || loading}
                icon={FaSave}
                className={`${(isSubmitting || loading) ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isSubmitting || loading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditCourse;
